import React, { useState, useEffect, useMemo } from "react";
import { Plus, Trash2, X, Layers, Save, RefreshCw, Search, AlertTriangle, Pencil } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Bg, Bt, Sl, Inp } from "./index";
import { confirmDialog, notify } from "../notify";
import { ComponentModal } from "./SalaryComponentsManager";
import { EmployeeCtcSummary } from "./EmployeeCtcSummary";

// Phase 3a — per-employee salary structure editor.
// Lives on the employee profile (Salary & Payroll tab). Lets you:
//   - apply a template (replace or merge)
//   - edit per-component values, add/remove components
//   - preview annual CTC, monthly gross, net pay
// Payroll still reads the legacy employee_salary columns until Phase 3b.

const CATEGORY_ACCENT = { earning: K.g, deduction: K.r, reimbursement: K.bl, benefit: K.pu };
const CATEGORY_LABEL  = { earning: 'Earnings', deduction: 'Deductions', reimbursement: 'Reimbursements', benefit: 'Benefits' };

const fmtINR = (n) => '₹' + Number(Math.round(n || 0)).toLocaleString('en-IN');
const fmtCalcLabel = (calc) =>
  calc === 'fixed' ? 'Fixed (₹/month)' :
  calc === 'percent_of_basic' ? '% of Basic' :
  calc === 'percent_of_ctc'   ? '% of CTC' :
  calc === 'percent_of_gross' ? '% of Gross' : calc;

// Given a structure (array of {componentCode, value, calcType, category}),
// compute monthly per-component amounts + annual CTC.
//
// If `annualCtcAnchor` is passed (HR-entered value), percent_of_ctc
// components use it directly. Otherwise the function back-solves CTC
// from the fixed components — useful when the employee doesn't have
// a CTC set yet.
const computeMonthly = (lines, annualCtcAnchor, statutory) => {
  const monthly = {};
  const earningsLines = lines.filter(l => l.category === 'earning' && l.active !== false);
  const deductionsLines = lines.filter(l => l.category === 'deduction' && l.active !== false);

  const basicLine = earningsLines.find(l => l.componentCode === 'BASIC');
  let basicMonthly = basicLine && basicLine.calcType === 'fixed' ? parseFloat(basicLine.value) || 0 : 0;
  const anchor = parseFloat(annualCtcAnchor) || 0;
  let ctcEstimate = 0;

  if (anchor > 0) {
    // Anchored — HR entered the CTC; treat as source of truth.
    ctcEstimate = anchor;
    if (basicLine && basicLine.calcType === 'percent_of_ctc') {
      basicMonthly = (parseFloat(basicLine.value) || 0) / 100 * anchor / 12;
    }
  } else {
    // Derived — back-solve CTC from fixed components.
    earningsLines.forEach(l => {
      const v = parseFloat(l.value) || 0;
      if (l.calcType === 'fixed') ctcEstimate += v * 12;
      else if (l.calcType === 'percent_of_basic') ctcEstimate += (basicMonthly * v / 100) * 12;
    });
    if (basicLine && basicLine.calcType === 'percent_of_ctc') {
      let ctc = ctcEstimate || 600000;
      for (let i = 0; i < 5; i++) {
        const basicAnnual = (parseFloat(basicLine.value) || 0) / 100 * ctc;
        basicMonthly = basicAnnual / 12;
        ctc = 0;
        earningsLines.forEach(l => {
          const v = parseFloat(l.value) || 0;
          if (l.calcType === 'fixed') ctc += v * 12;
          else if (l.calcType === 'percent_of_basic') ctc += basicAnnual * v / 100;
          else if (l.calcType === 'percent_of_ctc' && l.componentCode === 'BASIC') ctc += basicAnnual;
        });
        const otherPctOfCtc = earningsLines
          .filter(l => l.calcType === 'percent_of_ctc' && l.componentCode !== 'BASIC')
          .reduce((s, l) => s + (parseFloat(l.value) || 0), 0);
        if (otherPctOfCtc > 0) ctc = ctc / (1 - otherPctOfCtc / 100);
      }
      ctcEstimate = ctc;
    }
  }

  // Final pass — compute every component
  earningsLines.forEach(l => {
    const v = parseFloat(l.value) || 0;
    if (l.calcType === 'fixed') monthly[l.componentCode] = v;
    else if (l.calcType === 'percent_of_basic') monthly[l.componentCode] = basicMonthly * v / 100;
    else if (l.calcType === 'percent_of_ctc')   monthly[l.componentCode] = (ctcEstimate * v / 100) / 12;
    else monthly[l.componentCode] = 0;
  });
  const grossMonthly = earningsLines.reduce((s, l) => s + (monthly[l.componentCode] || 0), 0);
  // PF wage base = earnings flagged "considered for PF" (Basic, and e.g. DA), falling back to Basic;
  // payroll caps it at the PF wage ceiling, so the figure shown here equals the payslip.
  const pfBaseMonthly = earningsLines.filter(l => l.consideredForPf).reduce((s, l) => s + (monthly[l.componentCode] || 0), 0) || basicMonthly;
  // Resolve percent_of_gross deductions now
  deductionsLines.forEach(l => {
    const v = parseFloat(l.value) || 0;
    if (l.calcType === 'fixed') monthly[l.componentCode] = v;
    else if (l.calcType === 'percent_of_basic') monthly[l.componentCode] = basicMonthly * v / 100;
    else if (l.calcType === 'percent_of_gross') monthly[l.componentCode] = grossMonthly * v / 100;
    else if (l.calcType === 'percent_of_ctc')   monthly[l.componentCode] = (ctcEstimate * v / 100) / 12;
    else monthly[l.componentCode] = 0;
    // PF / ESIC: the rate on the row only counts when HR applied it as a custom rate; otherwise payroll
    // uses the standard statutory rate, so show that.
    if (l.componentCode === 'PF_EMP' && l.calcType === 'percent_of_basic') {
      const eff = l.customRate || statutory?.pfStd == null ? v : statutory.pfStd;
      monthly[l.componentCode] = statutory?.pfCeil > 0 ? Math.min(pfBaseMonthly, statutory.pfCeil) * eff / 100 : basicMonthly * eff / 100;
    }
    if (l.componentCode === 'ESI_EMP' && l.calcType === 'percent_of_basic') {
      const eff = l.customRate || statutory?.esiStd == null ? v : statutory.esiStd;
      monthly[l.componentCode] = basicMonthly * eff / 100;
    }
  });

  // Reimbursements / benefits — also surfaced in the Monthly column so the
  // editor isn't lying when the row says ₹0 for a fixed ₹5000 fuel cap.
  const resolveLine = (l) => {
    const v = parseFloat(l.value) || 0;
    if (l.calcType === 'fixed') return v;
    if (l.calcType === 'percent_of_basic') return basicMonthly * v / 100;
    if (l.calcType === 'percent_of_ctc')   return (ctcEstimate * v / 100) / 12;
    if (l.calcType === 'percent_of_gross') return grossMonthly * v / 100;
    return 0;
  };
  const reimbursementLines = lines.filter(l => l.category === 'reimbursement' && l.active !== false);
  const benefitLines       = lines.filter(l => l.category === 'benefit'       && l.active !== false);
  [...reimbursementLines, ...benefitLines]
       .forEach(l => { monthly[l.componentCode] = resolveLine(l); });

  const dedMonthly     = deductionsLines.reduce((s, l) => s + (monthly[l.componentCode] || 0), 0);
  const reimbMonthly   = reimbursementLines.reduce((s, l) => s + (monthly[l.componentCode] || 0), 0);
  const benefitsMonthly = benefitLines.reduce((s, l) => s + (monthly[l.componentCode] || 0), 0);

  // Net formula per the CHRO spec (HRMS New Updates):
  //   Monthly Net = Monthly Gross + Monthly Benefits − Monthly Deductions
  // Note this includes employer-side benefit components (PF_ER, gratuity
  // provision, GHI), so it is a total-value figure rather than the amount
  // credited to the employee's bank — the tile is labelled accordingly.
  const netMonthly = grossMonthly + benefitsMonthly - dedMonthly;

  // Components/yr must annualise the WHOLE structure, not earnings alone —
  // otherwise this disagrees with "Total Annual CTC" in the summary card
  // right below it, and the anchor-mismatch warning fires spuriously for
  // any structure that carries benefits or reimbursements.
  const annualCtc = (grossMonthly + reimbMonthly + benefitsMonthly) * 12;

  return { monthly, grossMonthly, dedMonthly, reimbMonthly, benefitsMonthly,
           netMonthly, annualCtc, basicMonthly };
};

export const EmployeeSalaryStructure = ({ employeeId, onSaved, canEditCtc = true, canEditPf = true, canEditEsi = true, canEditComponents = false, canDeleteComponents = false, canRaiseDeduction = false }) => {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [library, setLibrary] = useState([]);
  const [lines, setLines] = useState([]);
  const [annualCtc, setAnnualCtc] = useState('');
  const [picker, setPicker] = useState(false);
  const [applyTpl, setApplyTpl] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // Payslip snapshot list — used to warn HR that a structure change
  // won't retroactively update slips already generated for prior
  // periods (payslips are frozen at /process time).
  const [existingSlipPeriods, setExistingSlipPeriods] = useState([]);
  const [statutory, setStatutory] = useState(null);

  const load = () =>
    Promise.all([
      api.get('/api/employees/' + employeeId + '/salary-structure'),
      api.get('/api/salary-templates?active_only=1'),
      api.get('/api/salary-components?active_only=1'),
      api.get('/api/payroll/slips?employee_id=' + encodeURIComponent(employeeId)).catch(() => []),
    ]).then(([d, t, c, slips]) => {
      setData(d);
      setStatutory(d.statutory || null);
      setTemplates(t || []);
      setLibrary(c || []);
      setAnnualCtc(d.annualCtc > 0 ? String(d.annualCtc) : '');
      const periods = Array.isArray(slips) ? [...new Set(slips.map(s => s.period).filter(Boolean))].sort().reverse() : [];
      setExistingSlipPeriods(periods);
      setLines((d.components || []).map(x => ({
        componentCode: x.componentCode,
        name: x.name,
        category: x.category,
        calcType: x.calcType,
        consideredForPf: !!x.consideredForPf,
        customRate: !!x.customRate,
        value: String(x.value ?? 0),
        active: x.active !== false,
        sortOrder: x.sortOrder ?? 100,
      })));
    }).catch(e => setMsg('Error: ' + (e.message || 'load failed')));

  useEffect(() => { if (employeeId) load(); /* eslint-disable-next-line */ }, [employeeId]);
  const reloadLibrary = () => api.get('/api/salary-components?active_only=1').then(c => setLibrary(c || [])).catch(() => {});

  const flash = (m, e = false) => { setMsg((e ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 3500); };

  const addComponent = (libRow) => {
    if (lines.find(l => l.componentCode === libRow.code)) return;
    setLines(p => [...p, {
      componentCode: libRow.code, name: libRow.name, category: libRow.category,
      calcType: libRow.calcType, consideredForPf: !!libRow.consideredForPf, value: String(libRow.defaultValue ?? 0),
      active: true, sortOrder: 100 + p.length,
    }]);
  };
  const removeLine = (code) => setLines(p => p.filter(l => l.componentCode !== code));
  const setLineValue = (code, v) => setLines(p => p.map(l => l.componentCode === code ? { ...l, value: v } : l));
  // PF / ESIC rows: typing a % that differs from the standard applies it as a custom rate; going back to
  // the standard (or Reset) turns the override off.
  const setRate = (code, v, std) => setLines(p => p.map(l => l.componentCode === code
    ? { ...l, value: v, customRate: std != null && v !== '' && Math.abs((parseFloat(v) || 0) - std) > 1e-9 } : l));
  const setCustom = (code, on) => setLines(p => p.map(l => l.componentCode === code ? { ...l, customRate: on } : l));

  const grouped = useMemo(() => {
    const out = { earning: [], deduction: [], reimbursement: [], benefit: [] };
    lines.forEach(l => { (out[l.category] || (out[l.category] = [])).push(l); });
    return out;
  }, [lines]);

  const calc = useMemo(() => computeMonthly(lines, annualCtc, statutory), [lines, annualCtc, statutory]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/employees/' + employeeId + '/salary-structure', {
        templateId: data?.templateId || null,
        annualCtc: parseFloat(annualCtc) || 0,
        components: lines.map((l, i) => ({
          componentCode: l.componentCode,
          value: parseFloat(l.value) || 0,
          ...(['PF_EMP', 'ESI_EMP'].includes(l.componentCode) ? { customRate: !!l.customRate } : {}),
          active: l.active !== false,
          sortOrder: l.sortOrder || (100 + i),
        })),
      });
      flash('Structure saved');
      onSaved && onSaved();
      load();
    } catch (e) { flash(e.message || 'Save failed', true); }
    finally { setSaving(false); }
  };

  const applyTemplate = async (tplId, mode) => {
    setSaving(true);
    try {
      const r = await api.post('/api/employees/' + employeeId + '/salary-structure/apply-template', {
        templateId: tplId, mode,
      });
      flash(`Seeded ${r.seeded} components from template`);
      setApplyTpl(false);
      load();
    } catch (e) { flash(e.message || 'Apply failed', true); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="text-t3 text-[14px] p-4">Loading salary structure…</div>;

  return (
    <div>
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[15px] font-bold text-t0 flex items-center gap-2">
            Salary Structure
            <Bg c={K.g} sm dot>Used by Payroll</Bg>
          </div>
          <div className="text-[14px] text-t3 mt-[2px]">
            {data.templateName
              ? <>Applied from <b className="text-t1">{data.templateName}</b> ({data.templateCode}). Edits stay on this employee — re-applying overwrites.</>
              : <>No template applied. Pick one to seed a structure, or add components individually.</>}
          </div>
        </div>
        <div className="flex gap-2">
          <Bt s="sm" v="secondary" icon={RefreshCw} onClick={() => setApplyTpl(true)}>Apply Template</Bt>
          <Bt s="sm" icon={Save} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Bt>
        </div>
      </div>

      {/* Payslip snapshot warning — payslips already generated for this
          employee are frozen at process-time. A structure change here
          will apply automatically to the NEXT payroll run, but existing
          slips need HR to re-run /process for that period to refresh. */}
      {existingSlipPeriods.length > 0 && (() => {
        const shown = existingSlipPeriods.slice(0, 3).join(', ');
        const more  = existingSlipPeriods.length > 3 ? ` (+${existingSlipPeriods.length - 3} more)` : '';
        return (
          <div className="mb-3 p-3 rounded-lg bg-ab border border-ad flex items-start gap-2">
            <AlertTriangle size={13} color={K.a} className="flex-shrink-0 mt-[2px]" />
            <div className="text-[14px] text-t1 leading-[1.5]">
              <b className="text-t0">Payslips already exist for {shown}{more}.</b> Payslips are snapshots frozen at process time — your changes here will apply automatically to the <b>next payroll run</b>, but they won't retroactively update slips already generated. To refresh an existing period, re-run <b>Process Payroll</b> for that period, then re-generate the payslips.
            </div>
          </div>
        );
      })()}

      <div className="rounded-xl border border-b0 p-3 bg-bg2 mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[12px] font-bold uppercase tracking-wide text-t3 mb-[3px]">Annual CTC</div>
            <input
              type="number"
              value={annualCtc}
              onChange={e => setAnnualCtc(e.target.value)}
              disabled={!canEditCtc}
              title={canEditCtc ? '' : 'You do not have permission to change the Annual CTC'}
              placeholder="e.g. 800000"
              className="w-full bg-bg1 border border-b1 rounded-lg px-3 py-2 text-[16px] font-bold text-t0 font-mono focus:outline-none focus:border-bl"
            />
            <div className="text-[12px] text-t3 mt-[3px]">
              The anchor used by every <i>% of CTC</i> component (Basic, Special, …). Leave empty to back-solve from fixed components.
            </div>
          </div>
          {parseFloat(annualCtc) > 0 && (
            <div className="text-right">
              <div className="text-[12px] uppercase tracking-wide text-t3 font-bold">Monthly equiv.</div>
              <div className="text-[17px] font-extrabold text-t0 font-mono">{'₹' + Math.round((parseFloat(annualCtc) || 0) / 12).toLocaleString('en-IN')}</div>
            </div>
          )}
        </div>
        {/* Mismatch warning — when the anchor doesn't match the sum of
            components, the percentages don't add up to 100%. */}
        {parseFloat(annualCtc) > 0 && Math.abs(parseFloat(annualCtc) - calc.annualCtc) / parseFloat(annualCtc) > 0.01 && (
          <div className="mt-2 pt-2 border-t border-b0 text-[14px] text-a flex items-start gap-1">
            <span className="text-a font-bold">⚠</span>
            <span>
              Your components annualise to <b>{fmtINR(calc.annualCtc)}</b> — that's
              <b> {calc.annualCtc > parseFloat(annualCtc) ? '+' : '−'}{fmtINR(Math.abs(calc.annualCtc - parseFloat(annualCtc)))}</b> vs the anchor.
              Add or tune percent components so the total matches.
            </span>
          </div>
        )}
      </div>

      <SummaryStrip calc={calc} />

      <div className="flex items-center justify-between mb-2 mt-4 flex-wrap gap-2">
        <div className="text-[14px] font-bold text-t3 uppercase tracking-wide">Components</div>
        <div className="flex items-center gap-2 flex-wrap">
          {[['PF_EMP', 'PF', canEditPf], ['ESI_EMP', 'ESIC', canEditEsi]].map(([code, label, ok]) => {
            const lib = library.find(c => c.code === code);
            if (!lib || lines.some(l => l.componentCode === code)) return null;
            return <Bt key={code} s="xs" v="secondary" icon={Plus} disabled={!ok} onClick={() => addComponent(lib)}>Add {label} (on Basic)</Bt>;
          })}
          <Bt s="xs" icon={Plus} onClick={() => setPicker(true)}>Add component</Bt>
        </div>
      </div>

      {lines.length === 0 ? (
        <div className="rounded-xl border border-dashed border-b0 p-6 text-center text-t3 text-[14px]">
          <Layers size={22} className="inline mb-1 text-t3" /><br/>
          No components configured yet. Apply a template or add components individually.
        </div>
      ) : (
        <div className="grid gap-3">
          {['earning', 'deduction', 'reimbursement', 'benefit'].map(cat => {
            const list = grouped[cat] || [];
            if (list.length === 0) return null;
            return (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-[5px] h-[14px] rounded-full" style={{ background: CATEGORY_ACCENT[cat] }} />
                  <span className="text-[14px] font-bold text-t1">{CATEGORY_LABEL[cat]}</span>
                </div>
                <div className="rounded-lg border border-b0 overflow-hidden">
                  <table className="w-full border-collapse text-[14px]">
                    <thead><tr className="bg-bg2">
                      <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Code</th>
                      <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Name</th>
                      <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Type</th>
                      <th className="px-2 py-1 text-right text-t3 text-[12px] uppercase">Value</th>
                      <th className="px-2 py-1 text-right text-t3 text-[12px] uppercase">Monthly</th>
                      <th className="px-2 py-1 text-right text-t3 text-[12px] uppercase"></th>
                    </tr></thead>
                    <tbody>
                      {list.map(l => (
                        <tr key={l.componentCode} className="border-b border-b0">
                          <td className="px-2 py-1 font-mono text-[14px] text-t1">{l.componentCode}</td>
                          <td className="px-2 py-1 text-[14px] text-t0">{l.name}</td>
                          <td className="px-2 py-1 text-[14px] text-t2">{fmtCalcLabel(l.calcType)}</td>
                          <td className="px-2 py-1 text-right">
                            {(() => {
                              const isPf = l.componentCode === 'PF_EMP', isEsi = l.componentCode === 'ESI_EMP';
                              const std = isPf ? statutory?.pfStd : isEsi ? statutory?.esiStd : null;
                              const editable = isPf ? canEditPf : isEsi ? canEditEsi : true;
                              const differs = std != null && Math.abs((parseFloat(l.value) || 0) - std) > 1e-9;
                              return <>
                                <input
                                  type="number"
                                  value={l.value}
                                  disabled={!editable}
                                  title={editable ? '' : 'You do not have permission to change this percentage'}
                                  onChange={e => (std != null ? setRate(l.componentCode, e.target.value, std) : setLineValue(l.componentCode, e.target.value))}
                                  className="bg-bg2 border border-b1 rounded px-2 py-[3px] text-right text-[14px] text-t0 w-[110px] focus:outline-none focus:border-bl disabled:opacity-60"
                                />
                                {std != null && (
                                  <div className="text-[11.5px] mt-[2px] leading-tight" style={{ color: l.customRate ? '#B45309' : '#64748B' }}>
                                    {l.customRate
                                      ? <>Custom {l.value}% applied · standard {std}% <button type="button" disabled={!editable} onClick={() => setRate(l.componentCode, String(std), std)} className="underline bg-transparent border-0 cursor-pointer p-0 text-[11.5px]" style={{ color: '#2563EB' }}>reset</button></>
                                      : differs
                                        ? <>Payroll uses the standard {std}%; this {l.value}% is not applied <button type="button" disabled={!editable} onClick={() => setCustom(l.componentCode, true)} className="underline bg-transparent border-0 cursor-pointer p-0 text-[11.5px]" style={{ color: '#2563EB' }}>apply</button></>
                                        : <>Standard {std}% · follows Basic</>}
                                    {isPf && statutory?.pfCeil ? <div>on Basic up to ₹{Number(statutory.pfCeil).toLocaleString('en-IN')}</div> : null}
                                    {isEsi && statutory?.esiCeil ? <div>only while gross ≤ ₹{Number(statutory.esiCeil).toLocaleString('en-IN')}</div> : null}
                                  </div>
                                )}
                              </>;
                            })()}
                          </td>
                          <td className="px-2 py-1 text-right text-[14px] text-t1 font-mono">
                            {fmtINR(calc.monthly[l.componentCode] || 0)}
                          </td>
                          <td className="px-2 py-1 text-right">
                            <Bt v="ghost" s="xs" icon={Trash2} onClick={() => removeLine(l.componentCode)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {msg && <div className={`mt-3 text-[14px] font-semibold ${msg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{msg}</div>}

      {canRaiseDeduction && <ManualDeductions employeeId={employeeId} />}

      {/* Phase 5: full CTC view — reimbursements + benefits + take-home. */}
      {lines.length > 0 && (
        <div className="mt-5">
          <EmployeeCtcSummary
            annualCtcAnchor={annualCtc}
            structure={{
              components: lines.map(l => ({
                componentCode: l.componentCode,
                componentName: l.name,
                componentCategory: l.category,
                componentCalcType: l.calcType,
                value: parseFloat(l.value) || 0,
                active: l.active !== false,
              }))
            }}
          />
        </div>
      )}


      {picker && (
        <ComponentPicker
          library={library}
          alreadyAdded={new Set(lines.map(l => l.componentCode))}
          canEdit={canEditComponents} canDelete={canDeleteComponents} onChanged={reloadLibrary}
          onClose={() => setPicker(false)}
          onPick={(row) => { addComponent(row); setPicker(false); }}
        />
      )}
      {applyTpl && (
        <ApplyTemplateModal
          templates={templates}
          hasExisting={lines.length > 0}
          onClose={() => setApplyTpl(false)}
          onApply={applyTemplate}
        />
      )}
    </div>
  );
};

// ── Summary strip (monthly gross/benefits/deductions/net + components annualised) ──
const SummaryStrip = ({ calc }) => (
  <div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
      {[
        ['Components / yr', fmtINR(calc.annualCtc), K.bl, null],
        ['Monthly Gross', fmtINR(calc.grossMonthly), K.g, null],
        ['Monthly Benefits', fmtINR(calc.benefitsMonthly), K.pu, 'Employer-side'],
        ['Monthly Deductions', fmtINR(calc.dedMonthly), K.r, null],
        ['Monthly Net (est.)', fmtINR(calc.netMonthly), K.pu, 'Gross + Benefits − Ded.'],
      ].map(([label, val, accent, hint]) => (
        <div key={label} className="rounded-lg border border-b0 p-3 bg-bg2">
          <div className="flex items-center gap-2">
            <span className="w-[5px] h-[14px] rounded-full" style={{ background: accent }} />
            <div className="text-[12px] uppercase tracking-wide text-t3 font-bold">{label}</div>
          </div>
          <div className="text-[17px] font-extrabold text-t0 mt-[2px] font-mono">{val}</div>
          {hint && <div className="text-[12px] text-t3 mt-[1px]">{hint}</div>}
        </div>
      ))}
    </div>
    {/* Monthly Net here follows the configured CTC formula and includes
        employer contributions, so it is larger than the amount credited to
        the employee. Spell that out rather than letting HR read it as
        bank credit. */}
    {calc.benefitsMonthly > 0 && (
      <div className="text-[12px] text-t3 mt-2 leading-[1.4]">
        Monthly Net includes employer-side benefits (PF employer share, gratuity provision,
        insurance). Payroll credits{' '}
        <b className="text-t2 font-mono">{fmtINR(calc.grossMonthly + calc.reimbMonthly - calc.dedMonthly)}</b>
        {' '}— gross + reimbursements − deductions.
      </div>
    )}
  </div>
);

// ── Library picker (mirrors the one in SalaryTemplatesManager) ──
const ComponentPicker = ({ library, alreadyAdded, onClose, onPick, canEdit = false, canDelete = false, onChanged = () => {} }) => {
  const [editing, setEditing] = useState(null); // null | { } (new) | library row (edit)
  const del = async (c) => {
    const text = c.system
      ? `"${c.name}" is a system component and can't be deleted. Deactivate it instead? It will stop appearing here.`
      : `Delete component "${c.name}"? This cannot be undone. Employees who already have it keep their saved line.`;
    if (!(await confirmDialog(text))) return;
    try {
      const r = await api.del('/api/salary-components/' + c.code);
      notify(r.deleted ? 'Component deleted' : 'Component deactivated', { title: 'Saved', variant: 'success' });
      onChanged();
    } catch (e) { notify(e.message || 'Failed', { title: 'Error', variant: 'error' }); }
  };
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const filtered = library
    .filter(c => cat === 'all' || c.category === cat)
    .filter(c => !alreadyAdded.has(c.code))
    .filter(c => {
      if (!q.trim()) return true;
      const k = q.trim().toLowerCase();
      return c.name.toLowerCase().includes(k) || c.code.toLowerCase().includes(k);
    });

  const fmtDefault = (calc, val) =>
    calc === 'fixed' ? '₹' + Number(val || 0).toLocaleString('en-IN') :
    calc === 'percent_of_basic' ? `${val}% of Basic` :
    calc === 'percent_of_ctc'   ? `${val}% of CTC` :
    calc === 'percent_of_gross' ? `${val}% of Gross` : `${val}`;

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 w-full max-w-[936px] max-h-[88vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-2">
          <span className="text-[17px] font-extrabold text-t0">Pick a component</span>
          <div className="flex items-center gap-2">
            {canEdit && <Bt s="xs" icon={Plus} onClick={() => setEditing({})}>New component</Bt>}
            <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
          </div>
        </div>
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-t3" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or code…"
                   className="w-full bg-bg2 border border-b1 rounded pl-7 pr-2 py-[6px] text-[14px] text-t0 focus:outline-none focus:border-bl" />
          </div>
          <Sl options={[
            { v: 'all', l: 'All' }, { v: 'earning', l: 'Earnings' },
            { v: 'deduction', l: 'Deductions' }, { v: 'reimbursement', l: 'Reimbursements' },
            { v: 'benefit', l: 'Benefits' },
          ]} value={cat} onChange={setCat} />
        </div>
        <div className="rounded-lg border border-b0 overflow-hidden">
          <table className="w-full border-collapse text-[14px]">
            <thead><tr className="bg-bg2">
              <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Code</th>
              <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Name</th>
              <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Category</th>
              <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Default</th>
              <th className="px-2 py-1 text-right text-t3 text-[12px] uppercase"></th>
            </tr></thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.code} className="border-b border-b0 hover:bg-sh">
                  <td className="px-2 py-1 font-mono text-[14px] text-t1">{c.code}</td>
                  <td className="px-2 py-1 text-[14px] text-t0">{c.name}</td>
                  <td className="px-2 py-1 text-[14px]">
                    <Bg c={CATEGORY_ACCENT[c.category] || K.t3} sm>{(CATEGORY_LABEL[c.category] || c.category).replace(/s$/, '')}</Bg>
                  </td>
                  <td className="px-2 py-1 text-[14px] text-t2">{fmtDefault(c.calcType, c.defaultValue)}</td>
                  <td className="px-2 py-1 text-right whitespace-nowrap">
                    {canEdit && <button title="Edit component" onClick={() => setEditing(c)} className="bg-transparent border-0 text-t3 hover:text-t0 cursor-pointer mr-1 align-middle"><Pencil size={14} /></button>}
                    {canDelete && <button title="Delete component" onClick={() => del(c)} className="bg-transparent border-0 text-r cursor-pointer mr-2 align-middle"><Trash2 size={14} /></button>}
                    <Bt s="xs" onClick={() => onPick(c)}>Add</Bt>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center p-3 text-t3 text-[14px]">No matches.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {editing && <ComponentModal cur={editing.id ? editing : null} defaultCategory={editing.category || (cat !== 'all' ? cat : 'earning')} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
      </div>
    </div>
  );
};


// ── Manual deduction — HR keeps a note + amount for a one-off recovery (manager asked, damage, advance…).
//    Uses the same Deductions ledger as HR Corner, so approval limits, employee acknowledgement and the
//    payroll recovery all apply exactly as there; payroll shows and deducts it in the chosen month.
const ManualDeductions = ({ employeeId }) => {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get('/api/deductions?employee_id=' + encodeURIComponent(employeeId)).then(d => setRows(d.rows || [])).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);
  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  const STATUS_C = { Active: K.g, Pending: K.a, AwaitingAck: K.a, Completed: K.t3, Rejected: K.r, Reversed: K.r, Disputed: K.r };
  return (
    <div className="mt-5 pt-4 border-t border-b0">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[14px] font-bold text-t0">Manual deductions</div>
          <div className="text-[12px] text-t3">A one-off or instalment deduction with a description — shows in payroll and is taken from that month's pay.</div>
        </div>
        <Bt s="xs" icon={Plus} onClick={() => setOpen(true)}>Add deduction</Bt>
      </div>
      {rows && rows.length > 0 && (
        <div className="rounded-lg border border-b0 overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead><tr className="bg-bg2 text-t3 text-[12px] uppercase">
              <th className="px-2 py-1 text-left">Description</th><th className="px-2 py-1 text-left">Type</th>
              <th className="px-2 py-1 text-right">Amount</th><th className="px-2 py-1 text-right">Left</th>
              <th className="px-2 py-1 text-left">From</th><th className="px-2 py-1 text-left">Status</th>
            </tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id} className="border-b border-b0">
                <td className="px-2 py-1 text-t0">{r.reason}</td>
                <td className="px-2 py-1 text-t2">{r.deduction_type}</td>
                <td className="px-2 py-1 text-right">{fmt(r.total_amount)}</td>
                <td className="px-2 py-1 text-right">{fmt(r.remaining_amount)}</td>
                <td className="px-2 py-1 text-t2">{r.next_installment_month || r.end_month || '—'}</td>
                <td className="px-2 py-1"><Bg c={STATUS_C[r.status] || K.t3} sm>{r.status}</Bg></td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
      {open && <ManualDeductionModal employeeId={employeeId} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
};

const ManualDeductionModal = ({ employeeId, onClose, onDone }) => {
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [types, setTypes] = useState(['Other']);
  const [f, setF] = useState({ deduction_type: 'Other', reason: '', amount: '', month: thisMonth, split: false, n: '2' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { api.get('/api/deductions/settings').then(d => { const t = d?.deduction_types || d?.settings?.deduction_types; if (Array.isArray(t) && t.length) setTypes(t); }).catch(() => {}); }, []);
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const amt = parseFloat(f.amount) || 0;
  const n = Math.max(2, parseInt(f.n) || 2);
  const submit = async () => {
    setErr('');
    if (!f.reason.trim()) return setErr('Write a short description (who asked / why).');
    if (!(amt > 0)) return setErr('Enter an amount greater than 0.');
    if (!/^\d{4}-\d{2}$/.test(f.month)) return setErr('Pick the month to deduct from.');
    setBusy(true);
    try {
      const body = { employee_id: employeeId, deduction_type: f.deduction_type, reason: f.reason.trim(), total_amount: amt, start_month: f.month,
        recovery_mode: f.split ? 'installment' : 'one_time' };
      if (f.split) { body.num_installments = n; body.monthly_amount = Math.round((amt / n) * 100) / 100; }
      const r = await api.post('/api/deductions', body);
      notify(`Deduction of ₹${amt.toLocaleString('en-IN')} saved${r?.status ? ' — ' + r.status : ''}. It is taken in payroll from ${f.month}.`, { title: 'Saved', variant: 'success' });
      onDone();
    } catch (e) { setErr(e.message || 'Could not save the deduction'); }
    finally { setBusy(false); }
  };
  const fieldCls = 'w-full bg-bg2 border border-b1 rounded px-2 py-[6px] text-[14px] text-t0 focus:outline-none focus:border-bl';
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[220] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 w-full max-w-[480px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-3">
          <span className="text-[17px] font-extrabold text-t0">Add manual deduction</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>
        <div className="grid gap-3">
          <div><div className="text-[12px] text-t3 uppercase mb-1">Type</div>
            <Sl options={types.map(t => ({ v: t, l: t }))} value={f.deduction_type} onChange={v => upd('deduction_type', v)} /></div>
          <div><div className="text-[12px] text-t3 uppercase mb-1">Description <span className="text-r">*</span></div>
            <textarea rows={3} className={fieldCls} value={f.reason} onChange={e => upd('reason', e.target.value)} placeholder="e.g. Manager asked to deduct — unapproved absence on 12 Sep" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-[12px] text-t3 uppercase mb-1">Amount (₹) <span className="text-r">*</span></div>
              <input type="number" min="0" className={fieldCls} value={f.amount} onChange={e => upd('amount', e.target.value)} /></div>
            <div><div className="text-[12px] text-t3 uppercase mb-1">Deduct from payroll of</div>
              <input type="month" className={fieldCls} value={f.month} onChange={e => upd('month', e.target.value)} /></div>
          </div>
          <label className="flex items-center gap-2 text-[14px] text-t1 cursor-pointer">
            <input type="checkbox" checked={f.split} onChange={e => upd('split', e.target.checked)} /> Split into monthly instalments
            {f.split && <><input type="number" min="2" max="36" className={fieldCls + ' !w-[70px]'} value={f.n} onChange={e => upd('n', e.target.value)} /><span className="text-t3 text-[12px]">× ₹{amt > 0 ? (Math.round((amt / n) * 100) / 100).toLocaleString('en-IN') : 0}</span></>}
          </label>
          <div className="text-[12px] text-t3">Larger amounts need approval and the employee's acknowledgement, per the limits set in Settings → Payroll. It only starts deducting once active.</div>
          {err && <div className="text-[14px] text-r font-semibold">{err}</div>}
          <div className="flex justify-end gap-2">
            <Bt v="secondary" s="xs" onClick={onClose}>Cancel</Bt>
            <Bt s="xs" disabled={busy} onClick={submit}>{busy ? 'Saving…' : 'Save deduction'}</Bt>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Apply Template modal — pick a template, choose replace/merge ──
const ApplyTemplateModal = ({ templates, hasExisting, onClose, onApply }) => {
  const [tplId, setTplId] = useState('');
  const [mode, setMode] = useState('replace');

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-5 w-full max-w-[768px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-3">
          <span className="text-[17px] font-extrabold text-t0">Apply Salary Template</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>

        <div className="text-[14px] text-t2 mb-[3px]">Template</div>
        <Sl options={[{ v: '', l: 'Select a template…' }, ...templates.map(t => ({ v: String(t.id), l: t.name + ' (' + t.code + ')' }))]}
            value={tplId} onChange={setTplId} />

        {hasExisting && (
          <div className="mt-3">
            <div className="text-[14px] text-t2 mb-1">Behaviour</div>
            <label className="flex items-start gap-2 cursor-pointer mb-1">
              <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} className="mt-[3px]" />
              <div>
                <div className="text-[14px] text-t0 font-semibold">Replace existing components</div>
                <div className="text-[12px] text-t3">Wipes the employee's current components, then seeds from the template.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" checked={mode === 'merge'} onChange={() => setMode('merge')} className="mt-[3px]" />
              <div>
                <div className="text-[14px] text-t0 font-semibold">Merge — add only missing components</div>
                <div className="text-[12px] text-t3">Existing values are preserved; new components from the template are appended.</div>
              </div>
            </label>
          </div>
        )}

        <div className="flex gap-[6px] mt-4">
          <Bt full disabled={!tplId}
              onClick={async () => {
                if (mode === 'replace' && hasExisting &&
                    !(await confirmDialog('This will replace all existing components on this employee. Continue?'))) return;
                onApply(parseInt(tplId), mode);
              }}>
            Apply
          </Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};
